/* eslint-disable import-sort/imports */
import { createContext, useContext, useEffect, useMemo } from "react"
import type { ReactNode } from "react"
import * as nativeMethods from "@bridge/methods"
import type {
  BridgeResponse,
  NativeBridgeInterface,
  BridgeHandlerName,
  BridgeRequest,
} from "types/native-bridge"
import { callNativeMessage, resolveNativeResponse } from "./callNative"

type ResponseHandler = (res: BridgeResponse) => void
const responseHandlers: Partial<
  Record<BridgeHandlerName, (res: BridgeResponse) => void>
> = {}

type WebRequestHandler = (data?: unknown) => unknown | Promise<unknown>
const webRequestHandlers: Partial<
  Record<BridgeHandlerName, WebRequestHandler>
> = {}

/* Web↔App 양방향 요청·응답 처리*/
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface BridgeContextValue extends NativeBridgeInterface {
  /** App→Web 응답 callback 등록 */
  onResponse: (handlerName: BridgeHandlerName, handler: ResponseHandler) => void
  /** App→Web 요청 handler 등록 */
  onWebRequest: (
    handlerName: BridgeHandlerName,
    handler: WebRequestHandler,
  ) => void
}
const BridgeContext = createContext<BridgeContextValue | null>(null)

export function NativeBridgeProvider({ children }: { children: ReactNode }) {
  const bridge = useMemo<BridgeContextValue>(
    () => ({
      ...nativeMethods,
      onResponse(handlerName, handler) {
        responseHandlers[handlerName] = handler
      },
      onWebRequest(handlerName, handler) {
        webRequestHandlers[handlerName] = handler
      },
    }),
    [],
  )

  useEffect(() => {
    // 전역에 요청 메소드와, 네이티브에서 호출할 응답 콜백 등록
    window.BridgeHandler.postMessage = (raw: string) => {
      const message: BridgeRequest | BridgeResponse = JSON.parse(raw)

      // App→Web 응답 처리
      if (message.type === "response") {
        resolveNativeResponse(message)
        responseHandlers[message.handlerName]?.(message)
        responseHandlers[message.handlerName] = undefined
        return
      }

      // App→Web 요청 처리
      if (message.type === "request") {
        const handler = nativeMethods[message.handlerName]
        if (!handler) {
          console.log(`웹에 등록된 핸들러가 없습니다: ${message.handlerName}`)
          return
        }

        Promise.resolve(handler(message.requestId))
          .then((result) => {
            console.log(result)
          })
          .catch((err) => {
            callNativeMessage({
              type: "response",
              handlerName: message.handlerName,
              requestId: message.requestId,
              isSuccess: false,
              result: err?.message ?? String(err),
            })
          })
      }
    }
  }, [])

  return (
    <BridgeContext.Provider value={bridge}>{children}</BridgeContext.Provider>
  )
}

export function useNativeBridge(): BridgeContextValue {
  const bridge = useContext(BridgeContext)
  if (!bridge) {
    throw new Error("NativeBridgeProvider가 등록되어 있지 않습니다.")
  }
  return bridge
}
